import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, X, Package, User, Building, Handshake, ListTodo, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { taskStatusChip } from "@/lib/notif";

const LINK_TYPES = ["Asset", "Lead", "Client", "Broker"];
const LINK_ICON = { Asset: Package, Lead: User, Client: Building, Broker: Handshake };

const LINK_QUERY = {
  Asset: (q) => ["/assets", q ? { q } : {}],
  Lead: (q) => ["/leads", { client_type: "individual", ...(q ? { q } : {}) }],
  Client: (q) => ["/leads", { client_type: "institutional", ...(q ? { q } : {}) }],
  Broker: (q) => ["/brokers", q ? { q } : {}],
};
function recordName(type, r) {
  if (type === "Asset") return { name: r.name, code: r.asset_code };
  if (type === "Lead" || type === "Client") return { name: r.lead_name, code: r.lead_code };
  return { name: r.broker_name, code: null };
}

function RecordPicker({ type, valueId, valueName, onPick }) {
  const [q, setQ] = useState("");
  const [endpoint, params] = LINK_QUERY[type](q);
  const { data = [] } = useQuery({
    queryKey: ["note-link-search", type, q],
    queryFn: async () => (await api.get(endpoint, { params })).data,
    enabled: !!type && q.length >= 2,
  });
  const filtered = (Array.isArray(data) ? data : [])
    .map((r) => ({ id: r.id, ...recordName(type, r) }))
    .filter((r) => r.name)
    .slice(0, 8);

  if (valueId) {
    return (
      <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid="note-linked-selected">
        <span className="text-sm text-slate-800 truncate">{valueName || valueId}</span>
        <button type="button" onClick={() => onPick(null, null)} className="text-slate-400 hover:text-slate-700" data-testid="note-linked-clear">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${type}… (2+ chars)`} className="pl-8" data-testid="note-link-search" />
      </div>
      {q.length >= 2 && (
        <div className="mt-1 border border-slate-200 rounded-md max-h-44 overflow-y-auto divide-y divide-slate-50">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400" data-testid="note-link-noresults">No results</div>
          ) : (
            filtered.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onPick(r.id, r.name); setQ(""); }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                data-testid={`note-link-option-${r.id}`}
              >
                <span className="font-medium text-slate-900">{r.name}</span>
                {r.code && <span className="ml-2 font-mono-num text-[10px] text-slate-400">{r.code}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const emptyForm = {
  title: "", content: "", linked_record_type: null, linked_record_id: null,
  linked_record_name: null, reminder_datetime: null, is_pinned: false,
};

export default function NoteFormDialog({ open, onClose, note, prefillLink, lockLink, onConvert }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editing = !!note;
  const [form, setForm] = useState(emptyForm);
  const [dirty, setDirty] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      title: note?.title || "",
      content: note?.content || "",
      linked_record_type: note?.linked_record_type || prefillLink?.type || null,
      linked_record_id: note?.linked_record_id || prefillLink?.id || null,
      linked_record_name: note?.linked_record_name || prefillLink?.name || null,
      reminder_datetime: note?.reminder_datetime || null,
      is_pinned: note?.is_pinned || false,
    });
    setDirty(false);
  }, [open, note, prefillLink]);

  const autosize = useCallback(() => {
    const el = contentRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.max(el.scrollHeight, 220)}px`; }
  }, []);
  useEffect(() => { if (open) setTimeout(autosize, 30); }, [open, form.content, autosize]);

  // Warn on hard page unload only when there are unsaved changes.
  useEffect(() => {
    if (!open || !dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [open, dirty]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title,
        content: form.content,
        linked_record_type: form.linked_record_type,
        linked_record_id: form.linked_record_type ? form.linked_record_id : null,
        linked_record_name: form.linked_record_type ? form.linked_record_name : null,
        reminder_datetime: form.reminder_datetime,
        is_pinned: form.is_pinned,
      };
      if (editing) return (await api.patch(`/notes/${note.id}`, payload)).data;
      return (await api.post("/notes", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Note updated" : "Note saved");
      qc.invalidateQueries({ queryKey: ["notes"] });
      setDirty(false);
      onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const attemptClose = () => {
    if (dirty && !window.confirm("You have unsaved changes — leave anyway?")) return;
    onClose();
  };

  const LinkIcon = form.linked_record_type ? LINK_ICON[form.linked_record_type] : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && attemptClose()}>
      <DialogContent
        className="max-w-lg max-h-[92vh] overflow-y-auto max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:w-full max-sm:max-w-full max-sm:rounded-none max-sm:border-0"
        data-testid="note-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Note" : lockLink ? "Quick Note" : "New Note"}</DialogTitle>
          <DialogDescription>A private note, visible only to you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Note title (optional)" data-testid="note-title-input" />
          </div>
          <div>
            <Label>Content</Label>
            <textarea
              ref={contentRef}
              value={form.content || ""}
              onChange={(e) => { set("content", e.target.value); autosize(); }}
              placeholder="Write your note here..."
              autoFocus
              className="mt-1 w-full min-h-[220px] resize-none rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="note-content-input"
            />
          </div>

          {editing && note?.converted_task_id && (
            note.converted_task_deleted ? (
              <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-400" data-testid="note-form-task-chip-deleted">
                <CheckCircle2 className="h-3.5 w-3.5" /> Task <span className="italic">(deleted)</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { navigate(`/tasks/${note.converted_task_id}`); onClose(); }}
                title={note.converted_task_title || ""}
                className={`inline-flex max-w-full items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs hover:opacity-80 ${taskStatusChip(note.converted_task_status).cls}`}
                data-testid="note-form-task-chip"
              >
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Task · {taskStatusChip(note.converted_task_status).label}
              </button>
            )
          )}

          {lockLink ? (
            form.linked_record_name && (
              <div className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700" data-testid="note-locked-link">
                {LinkIcon && <LinkIcon className="h-4 w-4 text-slate-500" />}
                <span className="text-xs uppercase tracking-wide text-slate-400">{form.linked_record_type}</span>
                <span className="font-medium truncate">{form.linked_record_name}</span>
              </div>
            )
          ) : (
            <div>
              <Label>Link to Record</Label>
              <div className="grid grid-cols-3 gap-2">
                <Select
                  value={form.linked_record_type || "none"}
                  onValueChange={(v) => { set("linked_record_type", v === "none" ? null : v); set("linked_record_id", null); set("linked_record_name", null); }}
                >
                  <SelectTrigger data-testid="note-link-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No link</SelectItem>
                    {LINK_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`note-link-type-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="col-span-2">
                  {form.linked_record_type ? (
                    <RecordPicker
                      type={form.linked_record_type}
                      valueId={form.linked_record_id}
                      valueName={form.linked_record_name}
                      onPick={(id, name) => { set("linked_record_id", id); set("linked_record_name", name); }}
                    />
                  ) : (
                    <Input disabled placeholder="Select a type first" />
                  )}
                </div>
              </div>
            </div>
          )}

          <div className={lockLink ? "" : "grid grid-cols-2 gap-3"}>
            <div>
              <Label>Reminder</Label>
              <Input
                type="datetime-local"
                value={toLocalInput(form.reminder_datetime)}
                onChange={(e) => set("reminder_datetime", e.target.value ? new Date(e.target.value).toISOString() : null)}
                data-testid="note-reminder-datetime"
              />
            </div>
            {!lockLink && (
              <div className="flex items-end gap-2 pb-1">
                <Switch checked={!!form.is_pinned} onCheckedChange={(v) => set("is_pinned", v)} data-testid="note-pin-toggle" id="note-pin" />
                <Label htmlFor="note-pin" className="cursor-pointer">Pin this note</Label>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {editing && onConvert ? (
            <Button
              variant="outline"
              onClick={() => onConvert({ id: note?.id, ...form })}
              className="border-sky-200 text-sky-700 hover:bg-sky-50"
              data-testid="note-convert-to-task"
            >
              <ListTodo className="h-4 w-4 mr-1.5" /> Convert to Task
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={attemptClose} data-testid="note-cancel">Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !(form.content || "").trim()} data-testid="note-save">
              {save.isPending ? "Saving…" : "Save Note"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
